/** Thin wrapper over @simplewebauthn/browser: the backend (py_webauthn's
 *  options_to_json) already emits the exact camelCase JSON shape
 *  startRegistration/startAuthentication expect, so the wrappers only add the
 *  unknown→typed cast and one uniform, detail-free error surface (the backend
 *  deliberately makes every passkey failure indistinguishable — the console
 *  matches that). */

import { startAuthentication, startRegistration } from '@simplewebauthn/browser'
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser'

/** False outside secure contexts / browsers without WebAuthn. */
export const webauthnSupported: boolean =
  typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined'

/** navigator.credentials.get — passkey login / MFA passkey ceremony. */
export async function runPasskeyAuthentication(options: unknown): Promise<AuthenticationResponseJSON> {
  return startAuthentication({ optionsJSON: options as PublicKeyCredentialRequestOptionsJSON })
}

/** navigator.credentials.create — passkey enrollment. */
export async function runPasskeyRegistration(options: unknown): Promise<RegistrationResponseJSON> {
  return startRegistration({ optionsJSON: options as PublicKeyCredentialCreationOptionsJSON })
}

/** One uniform message for every ceremony failure: user-cancelled and timed-
 *  out ceremonies (NotAllowedError) read the same as genuine failures — no
 *  detail leaks, matching the backend's _PASSKEY_FAIL posture. */
export function passkeyErrorMessage(err: unknown): string {
  if (err instanceof DOMException && err.name === 'NotAllowedError') {
    return 'The passkey ceremony was cancelled or timed out.'
  }
  if (err instanceof DOMException && err.name === 'SecurityError') {
    return 'Passkeys are not available on this origin.'
  }
  return 'Passkey verification failed.'
}
